import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import PageWrapper from "../components/PageWrapper";
import { LanguageContext } from "../contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * DiamondGamePage component allows users to play a digital scratch card game
 * where they scratch to reveal hidden diamonds. The prize is awarded based on
 * the total number of diamonds revealed.
 *
 * Props:
 * - onClose: Function to call when the game is closed (e.g., navigate back to dashboard).
 * - balance: The current balance of the player.
 * - onBalanceUpdate: Function to update the player's balance in the parent component.
 */
const DiamondGamePage = ({ onClose, balance, onBalanceUpdate = () => {} }) => {
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();

  // State variables for game management
  const [loading, setLoading] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [prizeAmount, setPrizeAmount] = useState(0); // Client-side display prize
  const [serverGamePrize, setServerGamePrize] = useState(0); // Actual prize from server (for potential server-side validation)
  const [isScratched, setIsScratched] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [isReadyModalOpen, setIsReadyModalOpen] = useState(true);
  const [isReadyModalFadingOut, setIsReadyModalFadingOut] = useState(false);
  const [showEndGameModal, setShowEndGameModal] = useState(false);
  const [showPrizesList, setShowPrizesList] = useState(false);
  const [revealedContent, setRevealedContent] = useState(Array(12).fill(null)); // Stores content for revealed JSX elements (icons)
  const [diamondCount, setDiamondCount] = useState(0); // Tracks the number of diamonds revealed
  const [isGameFinishedApiCalled, setIsGameFinishedApiCalled] = useState(false);
  const [scratchCirclesState, setScratchCirclesState] = useState([]); // State to hold scratch circle positions

  // Refs
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef(null);
  const scratchThreshold = 70; // Percentage of an area that needs to be scratched
  const hiddenItems = useRef(Array(12).fill(null)); // Stores the actual diamond/no-diamond distribution (icon paths)
  const revealedContentRef = useRef(revealedContent); // Ref for stable callbacks

  // Effect to keep the revealedContentRef in sync with the revealedContent state
  useEffect(() => {
    revealedContentRef.current = revealedContent;
  }, [revealedContent]);

  // Game configuration
  const GAME_COST = 10; // Cost to play
  const NUM_CELLS = 12; // 3x4 grid
  const GRID_COLS = 3;
  const GRID_ROWS = 4;
  const CIRCLE_RADIUS = 50; // Radius for each scratchable circle
  const SYMBOL_SIZE = 50; // Size of the diamond/no-prize icon (used for image sizing)
  const HORIZONTAL_SPACING = 20; // Spacing between circles horizontally
  const VERTICAL_SPACING = 20; // Spacing between circles vertically

  // Icon paths (using absolute paths assuming they are in the public/icons folder)
  const DIAMOND_ICON = "/icons/diamond-1.png";
  const NO_PRIZE_ICON = "/icons/rock-icon.png";
  const SCRATCH_COVER_IMAGE = "/icons/gift-box.png";

  // Prize mapping: Prize amount based on the number of diamonds found
  // This is for client-side display and internal logic consistency,
  // the actual prize is determined by the server.
  const DIAMOND_COUNT_TO_PRIZE = useMemo(
    () => ({
      12: 500000.0,
      11: 200000.0,
      10: 125000.0,
      9: 10000.0,
      8: 5000.0,
      7: 1000.0,
      6: 500.0,
      5: 100.0,
      4: 50.0,
      3: 20.0,
      2: 15.0,
      1: 10.0,
      0: 0.0, // Added explicitly for cases of 0 diamonds found
    }),
    []
  );

  // Reverse mapping for client-side generation based on server prize.
  // This maps the actual server-determined prize (capped at 100) to the
  // minimum number of diamonds that would yield that prize on the frontend for display.
  const PRIZE_TO_DIAMOND_COUNT = useMemo(() => {
    const reverseMap = {};
    // Iterate through the DIAMOND_COUNT_TO_PRIZE to create a mapping from prize to minimum diamond count
    const sortedEntries = Object.entries(DIAMOND_COUNT_TO_PRIZE).sort(([, prizeA], [, prizeB]) => prizeA - prizeB);
    sortedEntries.forEach(([count, prize]) => {
      // If a prize is already mapped, ensure we keep the mapping to the *lowest* diamond count
      // that produces that prize.
      if (!(prize in reverseMap)) {
        reverseMap[prize] = parseInt(count, 10);
      }
    });
    return reverseMap;
  }, [DIAMOND_COUNT_TO_PRIZE]);


  // Translations for the Diamond Game
  const translations = useMemo(
    () => ({
      en: {
        title: "Diamond Scratch Card",
        loadingGame: "Loading game...",
        scratchToReveal: "Scratch to Reveal!",
        youWon: "You Won: {amount} Birr!",
        betterLuckNextTime: "Better Luck Next Time!",
        playAgain: "Play Again",
        goBack: "Go Back to Dashboard",
        birr: "Birr",
        etb: "ETB",
        scratchArea: "Scratch Area",
        prizeArea: "Prize Area",
        insufficientBalance: "Insufficient balance to play. Please top up.",
        networkError: "Network error. Please try again.",
        gameError: "An error occurred during the game. Please try again.",
        processing: "Processing...",
        playButton: "Play for {cost} Birr",
        readyToPlay: "Ready to Play?",
        // Updated game description to reflect the server-side jackpot cap
        gameDescription:
          "Each game costs {cost} Birr. Scratch the gift boxes to uncover hidden diamonds—the more diamonds you find (up to {maxDiamonds}), the bigger your reward! Find {maxDiamonds} diamonds and win {jackpotPrize} Birr!",
        prizes: "Prizes",
        play: "Play",
        cancel: "Cancel",
        scratchTheBoxes:
          "Scratch the gift boxes to reveal diamonds! The more diamonds you find, the bigger your prize!",
        gameStarted: "Game Started! Scratch the boxes!",
        congratulations: "Congratulations!",
        error: "Error",
        failedToStartGame: "Failed to start game. Please try again.",
        hidePrizes: "Hide Prizes",
        showPrizes: "Show Prizes",
        tryAgain: "Try again to hit the jackpot!",
        diamondsFound: "Diamonds Found: {count}/{total}", // New translation key
      },
      am: {
        title: "የአልማዝ ከሽከሽ እጣ",
        loadingGame: "ጨዋታ በመጫን ላይ...",
        scratchToReveal: "ለመግለጥ ይከሽክሹ!",
        youWon: "አሸንፈዋል: {amount} ብር!",
        betterLuckNextTime: "በሚቀጥለው ጊዜ መልካም ዕድል!",
        playAgain: "እንደገና ይጫወቱ",
        goBack: "ወደ ዳሽቦርድ ይመለሱ",
        birr: "ብር",
        etb: "ETB",
        scratchArea: "የመከሽከሻ ቦታ",
        prizeArea: "የሽልማት ቦታ",
        insufficientBalance: "መጫወት የማይችል በቂ ሒሳብ የለም። እባክዎ ይሙሉ.",
        networkError: "የኔትወርክ ስህተት። እባክዎ እንደገና ይሞክሩ።",
        gameError: "በጨዋታው ወቅት ስህተት ተከስቷል። እባክዎ እንደገና ይሞክሩ።",
        processing: "በማስኬድ ላይ...",
        playButton: "ለ{cost} ብር ይጫወቱ",
        readyToPlay: "ለመጫወት ዝግጁ ኖት?",
        gameDescription:
          "እያንዳንዱ ጨዋታ {cost} ብር ያስከፍላል። የተደበቁ አልማዞችን ለማግኘት የስጦታ ሳጥኖቹን ይከሽክሹ - ብዙ አልማዞችን ባገኙ ቁጥር (እስከ {maxDiamonds} ድረስ) ሽልማትዎ ይበልጣል! {maxDiamonds} አልማዞችን ያግኙ እና {jackpotPrize} ብር ያሸንፉ!",
        prizes: "ሽልማቶች",
        play: "ይጫወቱ",
        cancel: "ይቅር",
        scratchTheBoxes:
          "አልማዞችን ለመግለጥ የስጦታ ሳጥኖቹን ይከሽክሹ! ብዙ አልማዞችን ባገኙ ቁጥር ሽልማትዎ ይበልጣል!",
        gameStarted: "ጨዋታው ተጀምሯል! ሳጥኖቹን ይፋቁ!",
        congratulations: "እንኳን ደስ አለዎት!",
        error: "ስህተት",
        failedToStartGame: "ጨዋታውን ማስጀመር አልተሳካም። እባክዎ እንደገና ይሞክሩ።",
        hidePrizes: "ሽልማቶችን ደብቕ",
        showPrizes: "ሽልማቶችን አሳይ",
        tryAgain: "ጃክፖቱን ለመምታት እንደገና ይሞክሩ!",
        diamondsFound: "የተገኙ አልማዞች: {count}/{total}", // New translation key
      },
      or: {
        title: "Kaardii Qarqaraa Daayimandii",
        loadingGame: "Tapha fe'aa jira...",
        scratchToReveal: "Saaxiluuf Qarqari!",
        youWon: "Ati Mo'atte: {amount} Birr!",
        betterLuckNextTime: "Yeroo Itti Aanu Milkaa'ina Gaarii!",
        playAgain: "Deebii Taphadhu",
        goBack: "Gara Daashboordii Deebi'i",
        birr: "Birr",
        etb: "ETB",
        scratchArea: "Iddoo Qarqaraa",
        prizeArea: "Iddoo Badhaasaa",
        insufficientBalance: "Baay'ina gahaa hin qabdu. Mee guuttadhu.",
        networkError: "Dogoggora networkii. Mee deebii yaali.",
        gameError: "Dogoggorri tapha keessatti uumameera. Mee deebii yaali.",
        processing: "Hojjechaa jira...",
        playButton: "Qarshii {cost}f Taphadhu",
        readyToPlay: "Taphaaf Qophooftaa?",
        gameDescription:
          "Tapha tokkoo qarshii {cost} baasii qaba. Saanduqawwan kennaa qarqariitii fi daayimandii dhokataa saaxiluuf—daayimandii baay'ee argatte (hanga {maxDiamonds}tti), badhaasni kee guddaa ta'a! Daayimandii {maxDiamonds} hunda argadhuutii fi {jackpotPrize} Birr mo'adhu!",
        prizes: "Badhaasota",
        play: "Taphadhu",
        cancel: "Haqi",
        scratchTheBoxes:
          "Daayimandii saaxiluuf saanduqawwan kennaa qarqari! Daayimandii baay'ee argatte, badhaasni kee guddaa ta'a!",
        gameStarted: "Taphi Eegaleera! Saanduqawwan qarqari!",
        congratulations: "Baga Gammaddan!",
        error: "Dogoggora",
        failedToStartGame: "Tapha eegaluun hin danda'amne. Mee deebii yaali.",
        hidePrizes: "Badhaasota Dhoksi",
        showPrizes: "Badhaasota Agarsiisi",
        tryAgain: "Jaakpoottii argachuuf deebii yaali!",
        diamondsFound: "Daayimandii Argatte: {count}/{total}", // New translation key
      },
      ti: {
        title: "ኣልማዝ ፍሓቕ ካርታ",
        loadingGame: "ጸወታ ይጽዕን ኣሎ...",
        scratchToReveal: "ንምግላጽ ፍሓቕ!",
        youWon: "ኣቲ ተዓዊትኪ: {amount} ቅርሺ!",
        betterLuckNextTime: "ቀጺሉ ዝመጽእ ግዜ ዕድል ይግበረልኪ!",
        playAgain: "እንደገና ተጻወት",
        goBack: "ናብ ዳሽቦርድ ተመለስ",
        birr: "ብር",
        etb: "ETB",
        scratchArea: "ቦታ ምፍሓቕ",
        prizeArea: "ቦታ ሽልማት",
        insufficientBalance: "ዘይምእኩል ሒሳብ ንምጽዋት. እባኽካ ምላእ.",
        networkError: "ጌጋ ኔትወርክ. እባኻ እንደገና ይሞክሩ.",
        gameError: "ሓደ ጌጋ ኣብ እዋን ጸወታ ኣጋጢሙ. እባኻ እንደገና ይሞክሩ.",
        processing: "ይስራሕ ኣሎ...",
        playButton: "ን{cost} ቅርሺ ተጻወት",
        readyToPlay: "ንኽትጻወት ድሉው ድኻ?",
        gameDescription:
          "ነፍሲ ወከፍ ጸወታ {cost} ቅርሺ የኽፍል. ዝተሓብኡ ኣልማዛት ንምግላጽ ሳንዱቓት ህያብ ፍሓቕ—ብዙሕ ኣልማዝ ዝረኸብካዮ (እስከ {maxDiamonds} ድኻ), ሽልማትካ ይበል! ኩሎም {maxDiamonds} ኣልማዛት ረኺብካ {jackpotPrize} ቅርሺ ተዓወት!",
        prizes: "ሽልማቶች",
        play: "ተጻወት",
        cancel: "ሰርዝ",
        scratchTheBoxes:
          "ኣልማዞችን ለመግለጥ የስጦታ ሳጥኖቹን ይከሽክሹ! ብዙ አልማዞችን ባገኙ ቁጥር ሽልማትዎ ይበልጣል!",
        gameStarted: "ጸወታ ተጀሚሩ! ሳጥኖቹን ይፋቁ!",
        congratulations: "እንኳዕ ደስ በለኩም!",
        error: "ጌጋ",
        failedToStartGame: "ጸወታ ክጅምር ኣይከኣለን. እባኻ እንደገና ይሞክሩ.",
        hidePrizes: "ሽልማቶችን ደብቕ",
        showPrizes: "ሽልማቶችን አሳይ",
        tryAgain: "ጃክፖት ንምርካብ እንደገና ይሞክሩ!",
        diamondsFound: "ዝተረኽቡ ኣልማዛት: {count}/{total}", // New translation key
      },
      wo: {
        title: "Mallaqa Boorsaa Scratch Card",
        loadingGame: "Tapha woyitaa xaa'i...",
        scratchToReveal: "Qarqaqqa'a!",
        youWon: "Ne mo'ay: {amount} Birr!",
        betterLuckNextTime: "Wolqaamoytaa taay!",
        playAgain: "Deemmoo taay",
        goBack: "Dashboardi ba'a",
        birr: "Birr",
        etb: "ETB",
        scratchArea: "Qarqaqqa'a",
        prizeArea: "Badhaasaa",
        insufficientBalance: "Baay'ina gahaa hin qabdu. Mee guuttadhu.",
        networkError: "Network dogoggora. Mee deebii yaali.",
        gameError: "Tapha keessatti dogoggorri uumameera. Mee deebii yaali.",
        processing: "Hojjechaa jira...",
        playButton: "Qarshii {cost}f Taphadhu",
        readyToPlay: "Taphaaf Qophooftaa?",
        gameDescription:
          "Tapha tokkoo qarshii {cost} baasii qaba. Boorsaa malaqaa qarqariitii fi qarshii sadii walfakkaataa argadhu! Carraa kee qoruuf qophooftaa?",
        prizes: "Badhaasota",
        play: "Taphadhu",
        cancel: "Haqi",
        scratchTheIcons:
          "Akaakilee boorsaa malaqaa qarqari, yoo qarshii sadii walfakkaataa argatte, battalumaan badhaasa sana mo'atta!",
        gameStarted:
          "Taphi Eegaleera! Badhaasota argachuuf boorsaa malaqaa qarqari!",
        congratulations: "Baga Gammaddan!",
        error: "Dogoggora",
        failedToStartGame: "Tapha eegaluun hin danda'amne. Mee deebii yaali.",
        hidePrizes: "Badhaasota Dhoksi",
        showPrizes: "Badhaasota Agarsiisi",
        tryAgain: "Jaakpoottii argachuuf deebii yaali!",
      },
    }),
    []
  );

  const t = useCallback(
    (key, variables = {}) => {
      let translation =
        translations[language]?.[key] || translations.en[key] || key;
      for (const [placeholder, value] of Object.entries(variables)) {
        translation = translation.replace(`{${placeholder}}`, value);
      }
      return translation;
    },
    [language, translations]
  );

  // Function to initialize the canvas for scratching with multiple items
  const initializeCanvas = useCallback(
    (itemsToDraw, canvasWidth, canvasHeight) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) {
        return;
      }

      // Explicitly set canvas dimensions
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Set globalCompositeOperation to source-over for drawing the initial cover layer
      ctx.globalCompositeOperation = "source-over";

      const totalGridWidth =
        GRID_COLS * (CIRCLE_RADIUS * 2) + (GRID_COLS - 1) * HORIZONTAL_SPACING;
      const totalGridHeight =
        GRID_ROWS * (CIRCLE_RADIUS * 2) + (GRID_ROWS - 1) * VERTICAL_SPACING;

      const startX = (canvas.width - totalGridWidth) / 2;
      const startY = (canvas.height - totalGridHeight) / 2;

      const newScratchCircles = []; // Temporary array to build new circle data

      const loadImage = (url) =>
        new Promise((resolve) => {
          const img = new Image();
          img.src = url;
          img.onload = () => resolve(img);
          img.onerror = () => {
            resolve(null);
          };
        });

      // Load only the scratch cover image
      loadImage(SCRATCH_COVER_IMAGE).then((coverImageElement) => {
        // Draw the scratch-off cover images (gift boxes) on top.
        itemsToDraw.forEach((itemUrl, index) => { // itemsToDraw here is just for iterating over cells to define scratchable areas
          const col = index % GRID_COLS;
          const row = Math.floor(index / GRID_COLS);

          const centerX =
            startX +
            col * (CIRCLE_RADIUS * 2 + HORIZONTAL_SPACING) +
            CIRCLE_RADIUS;
          const centerY =
            startY +
            row * (CIRCLE_RADIUS * 2 + VERTICAL_SPACING) +
            CIRCLE_RADIUS;

          newScratchCircles.push({
            x: centerX,
            y: centerY,
            r: CIRCLE_RADIUS,
          });

          ctx.save();
          ctx.beginPath();
          ctx.arc(centerX, centerY, CIRCLE_RADIUS, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip(); // Clip to the circular area for drawing the cover image

          if (coverImageElement) {
            // Draw the cover image, scaled and positioned to fit the circle
            ctx.drawImage(
              coverImageElement,
              centerX - CIRCLE_RADIUS,
              centerY - CIRCLE_RADIUS,
              CIRCLE_RADIUS * 2,
              CIRCLE_RADIUS * 2
            );
          } else {
            // Fallback if cover image fails to load: draw a solid dark gray circle
            ctx.fillStyle = "rgba(100, 100, 100, 1)"; // Fully opaque fallback
            ctx.fill();
          }
          ctx.restore(); // Restore context to remove the clip path

          // Draw the borders for each scratchable circle (on top of the cover)
          ctx.save();
          ctx.beginPath();
          ctx.arc(centerX, centerY, CIRCLE_RADIUS, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(153, 153, 153, 0.8)"; // Subtle border
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();
        });

        // After all cover layers are drawn, set globalCompositeOperation to destination-out
        // for subsequent scratching operations.
        ctx.globalCompositeOperation = "destination-out";
        setScratchCirclesState(newScratchCircles); // Update state to trigger re-render of revealed items
      });

      setIsScratched(false);
      setRevealedContent(Array(NUM_CELLS).fill(null)); // Reset revealed content for new game
      setDiamondCount(0);
      isDrawing.current = false;
      lastPoint.current = null;
      setIsGameFinishedApiCalled(false);
    },
    [
      CIRCLE_RADIUS,
      NUM_CELLS,
      GRID_COLS,
      GRID_ROWS,
      HORIZONTAL_SPACING,
      VERTICAL_SPACING,
      SCRATCH_COVER_IMAGE,
    ]
  );

  // Function to get client coordinates relative to the canvas
  const getClientPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const point = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    return point;
  }, []);

  // Function to calculate scratch percentage for a specific circular area
  const calculateAreaScratchPercentage = useCallback((areaIndex) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return 0;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return 0;
    }

    const circle = scratchCirclesState[areaIndex]; // Use state here
    if (!circle) {
      return 0;
    }

    // Get image data for the specific circular area
    const imageData = ctx.getImageData(
      circle.x - circle.r,
      circle.y - circle.r,
      circle.r * 2,
      circle.r * 2
    );
    const pixels = imageData.data;
    let transparentPixels = 0;
    let totalPixelsInCircle = 0;

    // Iterate through pixels to count transparent ones within the circle
    for (let y = 0; y < circle.r * 2; y++) {
      for (let x = 0; x < circle.r * 2; x++) {
        const dist = Math.sqrt(
          Math.pow(x - circle.r, 2) + Math.pow(y - circle.r, 2)
        );
        if (dist <= circle.r) {
          totalPixelsInCircle++;
          const pixelIndex = (y * (circle.r * 2) + x) * 4;
          // Check alpha channel (index 3) for transparency
          if (pixels[pixelIndex + 3] < 128) { // Less than 128 (out of 255) is transparent
            transparentPixels++;
          }
        }
      }
    }
    const percentage = totalPixelsInCircle > 0
      ? (transparentPixels / totalPixelsInCircle) * 100
      : 0;
    return percentage;
  }, [scratchCirclesState]); // Dependency on scratchCirclesState

  // --- Event Handlers for Scratching ---
  const handleMouseDown = useCallback(
    (e) => {
      if (isScratched || !gameStarted) {
        return;
      }
      isDrawing.current = true;
      lastPoint.current = getClientPoint(e);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.globalCompositeOperation = "destination-out"; // Ensure this is set for scratching
        ctx.beginPath();
        ctx.arc(lastPoint.current.x, lastPoint.current.y, 20, 0, Math.PI * 2); // Initial dot
        ctx.fillStyle = "rgba(0,0,0,1)"; // Color doesn't matter with destination-out
        ctx.fill();
      }
    },
    [isScratched, gameStarted, getClientPoint]
  );

  const handleMouseMove = useCallback(
    (e) => {
      const currentRevealedContent = revealedContentRef.current;

      if (!isDrawing.current || isScratched || !gameStarted) return;
      e.preventDefault(); // Prevent scrolling on touch devices

      const currentPoint = getClientPoint(e);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }

      // Draw the scratch stroke
      ctx.globalCompositeOperation = "destination-out"; // Ensure this is set for scratching
      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineTo(currentPoint.x, currentPoint.y);
      ctx.strokeStyle = "rgba(0,0,0,1)"; // Color doesn't matter much with destination-out
      ctx.lineWidth = 40; // Size of the scratch "brush"
      ctx.lineCap = "round";
      ctx.stroke();
      lastPoint.current = currentPoint;

      // Check all cells for scratch percentage after each stroke
      scratchCirclesState.forEach((circle, cellIndex) => { // Use state here
        // Only process if this cell hasn't been fully revealed yet (i.e., content is null)
        if (currentRevealedContent[cellIndex] === null) {
          const currentScratchPercentage =
            calculateAreaScratchPercentage(cellIndex);

          if (currentScratchPercentage >= scratchThreshold) {
            ctx.save();
            ctx.globalCompositeOperation = "destination-out";
            ctx.beginPath();
            ctx.arc(circle.x, circle.y, circle.r + 5, 0, Math.PI * 2); // Slightly larger radius for full clear
            ctx.fill(); // Fill the circle to make it fully transparent
            ctx.restore();

            // Update the state that controls the rendering of the revealed content in JSX
            setRevealedContent((prev) => {
              const newContent = [...prev];
              newContent[cellIndex] = hiddenItems.current[cellIndex]; // Store the icon path
              return newContent;
            });

            // If the revealed item is a diamond, increment the count
            if (hiddenItems.current[cellIndex] === DIAMOND_ICON) {
              setDiamondCount((prevCount) => prevCount + 1);
            }
          }
        }
      });

      // After updating revealedContent, check if the game should end
      // Note: Use revealedContentRef.current for the most up-to-date state in callbacks
      const allAreasRevealed = revealedContentRef.current.every((val) => val !== null);
      if (allAreasRevealed && !isScratched) {
        setIsScratched(true);
        isDrawing.current = false; // Stop drawing
      }
    },
    [
      isScratched,
      gameStarted,
      getClientPoint,
      calculateAreaScratchPercentage,
      scratchThreshold,
      DIAMOND_ICON,
      scratchCirclesState, // Dependency on scratchCirclesState
    ]
  );

  const handleMouseUp = useCallback(() => {
    const currentRevealedContent = revealedContentRef.current;

    if (!isDrawing.current) return; // Only process if drawing was active
    isDrawing.current = false;
    lastPoint.current = null;
    const allAreasRevealed = currentRevealedContent.every((val) => val !== null);
    if (allAreasRevealed && !isScratched) {
      setIsScratched(true);
    }
  }, [isScratched]);

  const handleTouchMove = useCallback(
    (e) => {
      e.preventDefault(); // Prevent scrolling on touch devices
      handleMouseMove(e);
    },
    [handleMouseMove]
  );

  // Function to finish the game and claim winnings
  const handleFinishGame = useCallback(async () => {
    if (!cardId || !gameStarted || isGameFinishedApiCalled) {
      return; // Prevent multiple calls or calls before game start/card ID
    }

    setLoading(true);
    setIsGameFinishedApiCalled(true); // Mark API call as initiated

    // The prizeAmount sent to the server must be the actual prize determined by the server at game start.
    // The client-side diamondCount is used only to *display* the outcome, not to re-calculate the prize for the server.
    setPrizeAmount(serverGamePrize); // Update display prize based on actual server prize

    try {
      const payload = {
        id: cardId,
        prizeAmount: serverGamePrize, // Send the server-determined prize back for validation
      };
      const response = await axiosInstance.post("/scratch/finish", payload);

      if (response.data.success) {
        // Update balance with the actual prize from the server.
        // The prize from the server is 'serverGamePrize'
        onBalanceUpdate(balance + serverGamePrize);
      } else {
        toast.error(t("gameError"));
      }
    } catch (error) {
      toast.error(t("networkError"));
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Handle authentication errors by redirecting to login
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        navigate("/login");
      }
    } finally {
      setLoading(false);
      setShowEndGameModal(true); // Show modal immediately
    }
  }, [cardId, gameStarted, serverGamePrize, balance, onBalanceUpdate, isGameFinishedApiCalled, navigate, t]);

  // Helper function to generate hidden items based on target diamond count
  const generateHiddenItems = useCallback((targetCount) => {
    const items = Array(NUM_CELLS).fill(NO_PRIZE_ICON); // Start with all no-prize icons
    const diamondPositions = new Set();

    // Randomly place diamonds until targetCount is met
    while (diamondPositions.size < targetCount) {
      const randomIndex = Math.floor(Math.random() * NUM_CELLS);
      if (!diamondPositions.has(randomIndex)) {
        diamondPositions.add(randomIndex);
        items[randomIndex] = DIAMOND_ICON;
      }
    }
    return items;
  }, [NUM_CELLS, DIAMOND_ICON, NO_PRIZE_ICON]);

  // Function to start a new game round after modal confirmation
  const startGameConfirmed = useCallback(async () => {
    if (balance < GAME_COST) {
      toast.error(t("insufficientBalance"));
      navigate("/wallet"); // Redirect to wallet page if balance is insufficient
      return;
    }

    // Initiate fade-out for ready modal
    setIsReadyModalFadingOut(true);
    setShowEndGameModal(false); // Hide end game modal if visible
    setLoading(true);
    setGameStarted(false); // Temporarily set gameStarted to false during reset
    setPrizeAmount(0);
    setServerGamePrize(0); // Reset server prize
    setIsScratched(false);
    setRevealedContent(Array(NUM_CELLS).fill(null)); // Reset revealed content for new game
    setDiamondCount(0);
    setIsGameFinishedApiCalled(false); // Reset API call flag

    isDrawing.current = false;
    lastPoint.current = null;
    try {
      // No client-side prize determination here. The prize comes from the server.
      const payload = {
        gameType: "diamond", // Ensure this matches the ENUM on your backend
        cost: GAME_COST,
        // The 'prizeAmount' field is removed from the initial '/scratch/new' payload
        // as the backend determines it.
      };
      const response = await axiosInstance.post("/scratch/new", payload);

      if (response.data.success) {
        setCardId(response.data.card.id);
        const actualServerPrize = response.data.card.prize_amount;
        setServerGamePrize(actualServerPrize); // Store the actual prize from the server

        // Determine target diamond count based on the server-determined prize.
        // If the server prize is 0, target 0 diamonds. Otherwise, use PRIZE_TO_DIAMOND_COUNT.
        // For capped prizes (e.g., 100 Birr), this will map to a consistent display (e.g., 5 diamonds).
        const targetDiamondCount = actualServerPrize === 0 ? 0 : (PRIZE_TO_DIAMOND_COUNT[actualServerPrize] || 0);

        // Generate hidden items (diamonds/no-diamonds) to match the determined prize outcome
        const generatedItems = generateHiddenItems(targetDiamondCount);
        hiddenItems.current = generatedItems;

        // Get current canvas dimensions for initialization
        const canvas = canvasRef.current;
        const parent = canvas ? canvas.parentElement : null;
        const canvasWidth = parent ? parent.clientWidth : 500;
        const canvasHeight = parent ? parent.clientHeight : 650;

        // Initialize canvas with the generated items and current dimensions
        initializeCanvas(generatedItems, canvasWidth, canvasHeight);

        onBalanceUpdate(balance - GAME_COST); // Deduct cost from balance
        setGameStarted(true); // Set gameStarted to true to trigger canvas rendering
        toast.success(t("gameStarted")); // Inform user that game has started
      } else {
        // Handle specific error for insufficient balance
        if (response.status === 402) {
          toast.error(t("insufficientBalance"));
          navigate("/wallet"); // Redirect to wallet page
        } else {
          toast.error(t("failedToStartGame"));
        }
      }
    } catch (error) {
      // Catch network errors or other unexpected errors
      toast.error(t("networkError"));
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        navigate("/login");
      } else if (error.response?.status === 402) { // Re-check for 402 in catch block as well
        toast.error(t("insufficientBalance"));
        navigate("/wallet");
      }
    } finally {
      setLoading(false);
    }
  }, [
    t,
    balance,
    GAME_COST,
    onBalanceUpdate,
    navigate,
    NUM_CELLS,
    PRIZE_TO_DIAMOND_COUNT,
    generateHiddenItems,
    initializeCanvas, // Add initializeCanvas as a dependency
  ]);

  // Main useEffect for canvas event listeners and resize handling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Attach event listeners for mouse and touch interactions
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("mouseleave", handleMouseUp); // End drawing if mouse leaves canvas

    canvas.addEventListener("touchstart", handleMouseDown);
    canvas.addEventListener("touchmove", handleTouchMove);
    canvas.addEventListener("touchend", handleMouseUp);
    canvas.addEventListener("touchcancel", handleMouseUp); // Handle touch being interrupted

    // Handle resize: re-initialize canvas and update scratchCirclesState
    const handleResize = () => {
      // Only re-initialize if the game has started and hidden items are present
      if (gameStarted && hiddenItems.current.length > 0) {
        const parent = canvas.parentElement;
        const canvasWidth = parent ? parent.clientWidth : 500;
        const canvasHeight = parent ? parent.clientHeight : 650;
        initializeCanvas(hiddenItems.current, canvasWidth, canvasHeight);
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      // Clean up event listeners when component unmounts or dependencies change
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("mouseleave", handleMouseUp);

      canvas.removeEventListener("touchstart", handleMouseDown);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleMouseUp);
      canvas.removeEventListener("touchcancel", handleMouseUp);
      window.removeEventListener("resize", handleResize);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, handleTouchMove, initializeCanvas, gameStarted, hiddenItems]);


  // Handle the end of the ready modal fade-out animation
  const handleReadyModalTransitionEnd = useCallback(() => {
    if (isReadyModalFadingOut) {
      setIsReadyModalOpen(false); // Fully close modal after fade out
      setIsReadyModalFadingOut(false); // Reset fade-out state
    }
  }, [isReadyModalFadingOut]);

  // Add event listener for ready modal transition end
  useEffect(() => {
    const modalElement = document.getElementById("ready-modal");
    if (modalElement) {
      modalElement.addEventListener(
        "transitionend",
        handleReadyModalTransitionEnd
      );
      return () => {
        modalElement.removeEventListener(
          "transitionend",
          handleReadyModalTransitionEnd
        );
      };
    }
  }, [handleReadyModalTransitionEnd, isReadyModalOpen]); // Depend on isReadyModalOpen to re-attach listener if modal re-opens

  // Effect to call handleFinishGame when isScratched becomes true and game is started
  useEffect(() => {
    if (isScratched && gameStarted && !isGameFinishedApiCalled) {
      handleFinishGame();
    }
  }, [isScratched, gameStarted, handleFinishGame, isGameFinishedApiCalled]);

  console.log("DiamondGamePage Render: isReadyModalOpen", isReadyModalOpen); // Debugging line

  // Get the maximum prize from the DIAMOND_COUNT_TO_PRIZE for display in description
  const maxPrize = useMemo(() => {
    return Math.max(...Object.values(DIAMOND_COUNT_TO_PRIZE));
  }, [DIAMOND_COUNT_TO_PRIZE]);

  // Determine the number of diamonds that corresponds to the max capped prize (100 Birr)
  const maxDiamondsForDisplay = useMemo(() => {
    // Find the diamond count that yields the highest prize in the DIAMOND_COUNT_TO_PRIZE map
    // This assumes DIAMOND_COUNT_TO_PRIZE is ordered or that higher diamond counts map to higher or equal prizes
    let maxCount = 0;
    let currentMaxPrize = 0;
    for (const countStr in DIAMOND_COUNT_TO_PRIZE) {
        const count = parseInt(countStr, 10);
        const prize = DIAMOND_COUNT_TO_PRIZE[countStr];
        if (prize > currentMaxPrize) {
            currentMaxPrize = prize;
            maxCount = count;
        } else if (prize === currentMaxPrize && count > maxCount) {
            // If multiple counts yield the same max prize, pick the highest count for display
            maxCount = count;
        }
    }
    return maxCount;
  }, [DIAMOND_COUNT_TO_PRIZE]);

  return (
    <PageWrapper backgroundClass="bg-blue-900 text-white flex flex-col items-center pt-16">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <div className="flex flex-col items-center w-full min-h-[calc(100vh-4rem)] relative z-1">
        <h1 className="text-4xl font-bold mb-4 text-blue-300 drop-shadow-lg animate-fade-in">
          {t("title")}
        </h1>
        {loading && (
          <p className="text-lg text-gray-300 animate-pulse">
            {t("processing")}
          </p>
        )}

        {/* Ready to Play Modal */}
        {isReadyModalOpen && (
          <div
            id="ready-modal"
            className={`fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] p-4 transition-opacity duration-500 ${ // Changed z-index
              isReadyModalFadingOut ? "opacity-0" : "opacity-100"
            }`}
          >
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm sm:max-w-md lg:max-w-xl w-full text-center text-gray-800 animate-pop-in">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">
                {t("readyToPlay")}
              </h2>
              <p className="text-md mb-6">
                {t("gameDescription", {
                  cost: GAME_COST,
                  maxDiamonds: maxDiamondsForDisplay, // Changed from totalDiamonds to maxDiamonds
                  jackpotPrize: maxPrize.toLocaleString(), // Jackpot prize is now the capped max prize
                })}
              </p>
              {/* Prize Button and List */}
              <div className="mb-6">
                <button
                  onClick={() => setShowPrizesList((prev) => !prev)}
                  // Explicitly remove bg, border, shadow for the button
                  className="text-xl font-semibold text-gray-700 mb-2 flex items-center justify-center w-full py-2 px-4 rounded-lg transition-colors bg-transparent border-none shadow-none"
                >
                  {showPrizesList ? t("hidePrizes") : t("showPrizes")}{" "}
                  <span className="ml-2 text-blue-500">💎</span>
                </button>
                {showPrizesList && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-lg font-medium text-gray-700 mt-4">
                    {/* Dynamically list prizes from DIAMOND_COUNT_TO_PRIZE in ascending order of diamond count */}
                    {Object.entries(DIAMOND_COUNT_TO_PRIZE)
                      .filter(([count, prize]) => parseInt(count, 10) > 0) // Filter out 0 diamonds (if 0 is ever a key)
                      .sort(([countA, prizeA], [countB, prizeB]) => prizeA - prizeB) // Sort by prize value (ascending)
                      .filter(([, prize], index, self) => index === self.findIndex((p) => p[1] === prize)) // Filter unique prizes for display
                      .map(([count, prize], index) => (
                        <div
                          key={index}
                          className="flex justify-start items-center"
                        >
                          <span className="text-sm text-gray-500 mr-1">
                            {PRIZE_TO_DIAMOND_COUNT[prize]} 💎: {/* Display the associated diamond count */}
                          </span>
                          <span className="font-bold">
                            {prize.toLocaleString()}
                          </span>
                          <span className="ml-1">{t("etb")}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-4">
                <button
                  onClick={startGameConfirmed}
                  disabled={loading}
                  className={`bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-colors transform hover:scale-105 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? t("processing") : t("play")}
                </button>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-6 rounded-lg shadow-lg transition-colors transform hover:scale-105"
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Diamond Count Display - New Element */}
        {gameStarted && !isReadyModalOpen && (
          <div className="bg-blue-800 bg-opacity-70 text-white px-4 py-2 rounded-lg shadow-md mb-4 flex items-center">
            <span className="text-xl font-semibold mr-2">💎</span>
            <p className="text-lg font-medium">
              {t("diamondsFound", { count: diamondCount, total: NUM_CELLS })}
            </p>
          </div>
        )}

        {/* Description text moved outside the relative container */}
        <p className={`text-lg text-gray-300 mb-4 ${
            gameStarted && !isReadyModalOpen ? '' : 'hidden' // Hide this text as well
        }`}>
          {t("scratchTheBoxes")}
        </p>

        {/* Main scratch card container */}
        <div className={`relative w-[380px] h-[500px] sm:w-[450px] sm:h-[600px] md:w-[500px] md:h-[650px] lg:w-[550px] lg:h-[700px]${
            gameStarted && !isReadyModalOpen ? '' : 'hidden' // Hide the container if game not started or modal is open
        }`}>
          {/* Revealed Values (scratched balls) - Rendered individually as they are revealed */}
          {revealedContent.map((content, index) => {
            // Only render if the value is not null (i.e., it has been revealed)
            if (content === null) return null;
            const circle = scratchCirclesState[index]; // Use state here for accurate positioning
            if (!circle) return null;

            return (
              <div
                key={index}
                className="absolute rounded-full animate-pop-in glass-ball flex items-center justify-center text-center font-bold text-white overflow-hidden"
                style={{
                  left: `${circle.x - circle.r}px`,
                  top: `${circle.y - circle.r}px`,
                  width: `${circle.r * 2}px`,
                  height: `${circle.r * 2}px`,
                  fontSize: `${circle.r * 0.4}px`,
                  zIndex: 5, /* Placed below canvas, which will be z-10 */
                  position: "absolute",
                }}
              >
                {/* Sparkle div to display the image background */}
                <div className="absolute inset-0 sparkle rounded-full pointer-events-none" style={{ backgroundImage: `url(/icons/sparkle.gif)` }} />
                {content === DIAMOND_ICON ? (
                    <img src={DIAMOND_ICON} alt="diamond" className="w-3/4 h-3/4 object-contain z-10" />
                ) : (
                    <img src={NO_PRIZE_ICON} alt="no prize" className="w-3/4 h-3/4 object-contain z-10" />
                )}
              </div>
            );
          })}
          <canvas
            ref={canvasRef}
            className={`absolute inset-0 w-full h-full touch-none z-10`} // Canvas is now z-10
            aria-label={t("scratchArea")}
          ></canvas>
        </div>
        {/* End Game Modal - Only visible after scratching is complete */}
        {showEndGameModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] p-4"> {/* Changed z-index */}
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full text-center text-gray-800 animate-pop-in">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">
                {prizeAmount > 0
                  ? t("congratulations")
                  : t("betterLuckNextTime")}
              </h2>
              <p className="text-md mb-6">
                {prizeAmount > 0
                  ? t("youWon", { amount: prizeAmount.toFixed(2) })
                  : t("tryAgain")}
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={startGameConfirmed}
                  disabled={loading}
                  className={`bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-colors transform hover:scale-105 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? t("processing") : t("playAgain")}
                </button>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-purple-900 hover:bg-purple-800 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-colors transform hover:scale-105"
                >
                  {t("goBack")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>
        {`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }\
          }
          .animate-fade-in { animation: fade-in 1s ease-out; }
          .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }

          /* New CSS for fade-out animation */
          .fade-out {
              opacity: 0;
              transition: opacity 1s ease-out; /* Adjust duration as needed */
          }
          /* New CSS for pop-in animation */
          @keyframes pop-in {
            0% { transform: scale(0.5); opacity: 0; }
            80% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-pop-in {
            animation: pop-in 0.4s ease-out forwards;
          }

          /* CSS for the revealed glass balls */
          .glass-ball {
              background: rgba(255, 255, 255, 0.2); /* Semi-transparent white */
              border: 2px solid rgba(255, 255, 255, 0.5);
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
              backdrop-filter: blur(5px); /* Glass effect */
              -webkit-backdrop-filter: blur(5px); /* For Safari */
          }

          .sparkle {
              background-size: cover;
              background-position: center;
              opacity: 0.7; /* Adjust as needed for sparkle intensity */
          }
        `}
      </style>
    </PageWrapper>
  );
};

export default DiamondGamePage;
